import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from './useToast';

interface DepartmentMetrics {
  totalMembers: number;
  activeMembers: number;
  performance: number;
}

export const useDepartmentMetrics = (departmentId: string) => {
  const [metrics, setMetrics] = useState<DepartmentMetrics>({
    totalMembers: 0,
    activeMembers: 0,
    performance: 85
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const isMounted = useRef(true);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  const fetchMetrics = useCallback(async (retryCount = 0) => {
    if (!departmentId || !isMounted.current) return;
    
    try {
      setIsLoading(true);
      setError(null);

      // Get department workers with their user details
      const { data: workers, error: workersError } = await supabase
        .from('users')
        .select(`
          id,
          status
        `)
        .eq('department_id', departmentId)
        .eq('role', 'worker');

      if (workersError) throw workersError;

      // Calculate metrics
      const totalMembers = workers?.length || 0;
      const activeMembers = workers?.filter(w => w.status === 'active')?.length || 0;

      if (isMounted.current) {
        setMetrics({
          totalMembers,
          activeMembers,
          performance: 85 // This could be calculated based on actual metrics
        });
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      if (isMounted.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
        
        // Retry logic with exponential backoff
        if (retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          retryTimeoutRef.current = setTimeout(() => {
            fetchMetrics(retryCount + 1);
          }, delay);
        }
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [departmentId, toast]);

  useEffect(() => {
    isMounted.current = true;

    // Initial fetch
    fetchMetrics();

    // Set up realtime subscription
    channelRef.current = supabase
      .channel(`department_metrics_${departmentId}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'users',
          filter: `department_id=eq.${departmentId}`
        },
        () => {
          if (isMounted.current) {
            fetchMetrics();
          }
        }
      )
      .subscribe();

    // Cleanup function
    return () => {
      isMounted.current = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [departmentId, fetchMetrics]);

  return {
    metrics,
    isLoading,
    error,
    refetch: fetchMetrics
  };
};